import { useState } from "react";
import { useSession } from "@core/session/SessionProvider";
import { ApiError } from "@core/http";
import { markErrorHandled } from "@portal/common/errorMessage";
import type { Template } from "@core/types";
import { tokens } from "@core/theme/tokens";
import {
  Badge,
  DialogHeader,
  ListingTablePanel,
  Modal,
  PortalButton,
  RowActionsCell,
  RowEditButton,
  Toggle,
  useToast,
} from "@portal/common";
import { buildEditInputFromRecord, type TemplateEditInput } from "./api";
import { useTemplateMutations, useTemplates } from "./messageTemplateQueries";
import { parameterMissingMessage } from "./messageTemplateSchemas";
import { TemplateEditor } from "./TemplateEditor";

// SPEC-218 §10: textos literales de las dos confirmaciones. No se toman del
// `title` que devuelve el servidor (SPEC-201 §4.4: ningún mensaje técnico
// crudo llega a la pantalla) — se fijan acá, igual que
// `MESSAGE_TEMPLATE_CONFIRM_*_TITLE` los fija api-http del lado del
// servidor.
const CONFIRM_CRITICAL_MESSAGE =
  "Esta plantilla sostiene el acceso al sistema. Un error impedirá ingresar. ¿Confirmas?";
const CONFIRM_DEACTIVATE_MESSAGE =
  "Los miembros dejarán de recibir este aviso. ¿Confirmas?";
const CRITICAL_NOT_DEACTIVATABLE_MESSAGE =
  "Las plantillas de acceso no se pueden desactivar";

// `portal.message-templates.confirm-critical-title` y
// `.confirm-deactivate-title` (screens.json) son elementos dinámicos
// separados con `region` propia (confirm-critical / confirm-deactivate):
// dos confirmaciones sobre la misma acción real ("guardar") pero disparadas
// por rechazos distintos del servidor. Reusar el título de "guardar" para
// desactivar era incoherente con la acción que se confirmaba
// (design-reviewer, intento 2, MessageTemplatesScreen.tsx:81).
function confirmCriticalTitle(templateName: string): string {
  return `¿Guardar cambios en «${templateName}»?`;
}
function confirmDeactivateTitle(templateName: string): string {
  return `¿Desactivar «${templateName}»?`;
}

function parameterMissingMessageFromError(error: ApiError): string {
  const problem = error.problem as
    { parameter?: string; language?: "es" | "en" } | undefined;
  // SPEC-218 §10: "El cuerpo debe incluir {parametro}. Sin él, el mensaje no
  // sirve" — literal, con el nombre real del parámetro. Mismo texto que
  // arma `parameterMissingMessage` de `messageTemplateSchemas.ts` para el
  // pre-chequeo del cliente (`TemplateEditor.submit`).
  return parameterMissingMessage(problem?.parameter ?? "?");
}

function formatUpdated(template: Template): string {
  const by = template.updatedBy ?? "—";
  if (!template.updatedAt) return by;
  const date = new Date(template.updatedAt);
  if (Number.isNaN(date.getTime())) return by;
  const formatted = `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;
  return `${by} · ${formatted}`;
}

const CHANNEL_LABEL: Record<Template["channel"], string> = {
  email: "Correo",
  push: "Push",
};

// SPEC-218 §5/§6, criterios 5 y 7: la confirmación adicional de guardar una
// plantilla crítica y la de desactivar una no crítica comparten la misma
// forma (título dinámico con el nombre de la plantilla + el texto literal
// de §10 + Cancelar/confirmar) — no hay un conteo de impacto que mostrar
// (a diferencia de `ImpactConfirmDialog`, pensado para inactivar ofertas
// enlazadas), así que el diálogo es propio en vez de forzar ese componente.
function TemplateConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  onCancel,
  onConfirm,
  busy,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  if (!open) return null;
  return (
    // Revisión en vivo 2026-09-22, puntos #3/#4A/#15: las tres regiones del
    // `Modal`, con la misma tipografía de encabezado que `ImpactConfirmDialog`
    // —el diálogo corto ya consolidado, del que este solo se diferencia por no
    // tener un conteo de impacto que mostrar—. Los márgenes que separaban
    // título, caja y botones se borran: ese aire lo ponen ahora los divisores
    // y el `padding` de cada región.
    <Modal
      label={title}
      width={460}
      onClose={onCancel}
      role="alertdialog"
      // Punto #21 (revisión en vivo 2026-09-22): encabezado común de diálogos.
      header={<DialogHeader eyebrow="Plantillas de mensaje" title={title} />}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <PortalButton
            type="button"
            tone="secondary"
            size="small"
            disabled={busy}
            onClick={onCancel}
          >
            Cancelar
          </PortalButton>
          <PortalButton
            type="button"
            size="small"
            disabled={busy}
            aria-busy={busy}
            onClick={onConfirm}
          >
            {busy ? "Confirmando…" : confirmLabel}
          </PortalButton>
        </div>
      }
    >
      <div
        style={{
          font: `500 13px ${tokens.font.ui}`,
          color: tokens.text.secondary,
          background: tokens.surface.surface,
          border: `1px solid ${tokens.surface.line}`,
          borderRadius: 12,
          padding: "12px 14px",
        }}
      >
        {message}
      </div>
    </Modal>
  );
}

// `ListingTablePanel` siempre agrega un octavo track de header ("Acciones")
// y un octavo hijo de fila (`RowActionsCell`) además de los 7 declarados
// abajo — headers.length(7) + 1, mismo criterio que OffersScreen.tsx:544
// (5 + 1) y BenefitsScreen.tsx:580 (4 + 1). Con solo 7 tracks el header
// "Acciones" se desbordaba a una fila implícita y "Editar" caía debajo del
// contenido en vez de alinearse a la derecha de la fila (design-reviewer,
// intento 2).
const COLS =
  "minmax(210px,1.4fr) 130px 90px minmax(220px,1.6fr) 90px 130px 190px 130px";

export function MessageTemplatesScreen() {
  const toast = useToast();
  const { hasPermission } = useSession();
  const templates = useTemplates();
  const mutations = useTemplateMutations();
  const [editing, setEditing] = useState<Template | null>(null);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  // Caso real (trace f4575316-5c75-4c27-83d0-340db0aafd9f, mismo patrón que
  // `CatalogsScreen.tsx`): el 400 con `errors[].field` (asunto solo de
  // correo, forma bilingüe inválida) se lo pasamos a `TemplateEditor` para
  // que intente asociarlo a un input con `applyApiFieldErrors`; si no puede,
  // nos avisa por `onUnhandledSubmitError` y ahí sí sale el toast genérico.
  const [submitError, setSubmitError] = useState<unknown>(undefined);
  const [pendingConfirm, setPendingConfirm] = useState<{
    code: string;
    name: string;
    kind: "CONFIRM_CRITICAL" | "CONFIRM_DEACTIVATE";
    input: TemplateEditInput;
    closeEditorOnSuccess: boolean;
  } | null>(null);

  const canEdit = hasPermission("content", "canEdit");
  const rows = templates.data ?? [];

  // Sin código de negocio reconocido y sin campo asociable, es un error de
  // servicio genérico — mismo literal y tono que antes de este fix
  // (PLAN-009 §3); el único cambio es que ahora es el editor quien decide,
  // vía `onUnhandledSubmitError`, si de verdad no pudo mostrarlo en un campo.
  const handleUnhandledSubmitError = (error: unknown) => {
    toast.show("No se pudo guardar la plantilla. Intenta nuevamente.", "error");
    markErrorHandled(error);
  };

  const onSaved = (template: Template, closeEditor: boolean) => {
    if (closeEditor) setEditing(null);
    setSaveError(undefined);
    setSubmitError(undefined);
    setPendingConfirm(null);
    toast.show(`${template.name} guardada`);
    // SPEC-218 §4.3/§10, criterio 10: aviso, no bloqueo — el servidor solo
    // lo calcula para las plantillas bilingües (PA-036-D).
    for (const warning of template.warnings ?? [])
      toast.show(warning.message, "neutral");
  };

  // SPEC-218 §5/§6, criterios 5 y 7: la primera llamada nunca manda
  // `confirmed`. Si el servidor responde 409, se abre el diálogo con el
  // texto exacto de §10 y se reintenta con `confirmed: true` — el mismo
  // endpoint, sin uno segundo.
  const attemptSave = (
    template: Template,
    input: TemplateEditInput,
    closeEditorOnSuccess: boolean,
  ) => {
    const code = template.code;
    setSaveError(undefined);
    setSubmitError(undefined);
    mutations.edit.mutate(
      { code, input: { ...input, confirmed: false } },
      {
        onSuccess: (updated) => onSaved(updated, closeEditorOnSuccess),
        onError: (error) => {
          if (!(error instanceof ApiError)) return;
          if (
            error.code === "CONFIRM_CRITICAL" ||
            error.code === "CONFIRM_DEACTIVATE"
          ) {
            setPendingConfirm({
              code,
              name: template.name,
              kind: error.code,
              input,
              closeEditorOnSuccess,
            });
            markErrorHandled(error);
            return;
          }
          if (error.code === "CRITICAL_NOT_DEACTIVATABLE") {
            // Rechazo de regla de negocio (una plantilla crítica de acceso
            // no se desactiva nunca), no un fallo del servicio: se queda en
            // tono `neutral` (PLAN-009 §3, mismo criterio que COST_INVALID
            // en RewardsScreen).
            toast.show(CRITICAL_NOT_DEACTIVATABLE_MESSAGE, "neutral");
            markErrorHandled(error);
            return;
          }
          if (error.code === "PARAMETER_MISSING") {
            // El código identifica el cuerpo (el parámetro que falta es de
            // ese campo): va como error de campo, no al toast.
            setSaveError(parameterMissingMessageFromError(error));
            markErrorHandled(error);
            return;
          }
          // `INVALID_REQUEST` (asunto solo de correo, forma bilingüe
          // inválida): se lo pasamos al editor por `submitError` para que
          // intente asociarlo a `subjectEs`/`bodyEs` — `markErrorHandled` NO
          // se llama acá, lo decide el `useEffect` de `TemplateEditor` según
          // si pudo aplicarlo o no.
          setSubmitError(error);
        },
      },
    );
  };

  const confirmPending = () => {
    if (!pendingConfirm) return;
    mutations.edit.mutate(
      {
        code: pendingConfirm.code,
        input: { ...pendingConfirm.input, confirmed: true },
      },
      {
        onSuccess: (template) =>
          onSaved(template, pendingConfirm.closeEditorOnSuccess),
        onError: (error) => {
          toast.show(
            "No se pudo confirmar el cambio. Intenta nuevamente.",
            "error",
          );
          markErrorHandled(error);
        },
      },
    );
  };

  const toggleActive = (template: Template) => {
    attemptSave(
      template,
      buildEditInputFromRecord(template, { active: !template.active }),
      false,
    );
  };

  const deactivateFromEditor = (template: Template) => {
    attemptSave(
      template,
      buildEditInputFromRecord(template, { active: false }),
      false,
    );
  };

  return (
    <div>
      {/* Título de página genérico, mismo nivel que replican todas las
        pantallas de nivel superior del portal (requests/RequestsScreen.tsx,
        offers/rewards/benefits vía "Gestión de Premios"). Nació para destapar
        la tabla de los chips «Validador QR»/«Volver a la app» que
        `PortalShell.tsx` posicionaba en `position: absolute` sobre `<main>`
        (design-reviewer, intento 2); esos chips se eliminaron en la revisión
        en vivo del 2026-09-22 (punto #5), así que el encabezado se conserva
        por la razón que hoy vale: la consistencia de las pantallas de nivel
        superior, y que sin él la fila de encabezado de la tabla arranca
        pegada al borde superior de `<main>`. */}
      <header style={{ marginBottom: 0 }}>
        <h1 style={{ margin: 0, font: `300 34px ${tokens.font.display}` }}>
          Plantillas de mensaje
        </h1>
        <p
          style={{
            color: tokens.text.tertiary,
            margin: "2px 0 26px",
            fontSize: 13,
          }}
        >
          Correos y notificaciones automáticas que el sistema envía a los
          miembros.
        </p>
      </header>

      <ListingTablePanel
        loading={templates.isLoading}
        rows={rows}
        columns={COLS}
        headers={[
          "Plantilla",
          "Código",
          "Canal",
          "Cuándo se envía",
          "Crítica",
          "Estado",
          "Actualizada por · cuándo",
        ]}
        empty={
          <div style={{ padding: 24, color: tokens.text.tertiary }}>
            No hay plantillas.
          </div>
        }
        renderRow={(template) => {
          const canToggle = canEdit && !template.isCritical;
          return (
            <div
              key={template.code}
              style={{
                display: "grid",
                gridTemplateColumns: COLS,
                gap: 14,
                alignItems: "center",
                padding: "13px 24px",
                borderBottom: `1px solid ${tokens.surface.line}`,
                opacity: template.active ? 1 : 0.55,
              }}
            >
              <span
                style={{
                  font: `600 12.5px/1.35 ${tokens.font.ui}`,
                  color: tokens.text.primary,
                }}
              >
                {template.name}
              </span>
              <span
                style={{
                  font: `400 12px/1.5 ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                {template.code}
              </span>
              <span
                style={{
                  font: `400 12px/1.5 ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                }}
              >
                {CHANNEL_LABEL[template.channel]}
              </span>
              <span
                style={{
                  font: `400 12px/1.5 ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                }}
              >
                {template.description}
              </span>
              <span>
                {template.isCritical ? (
                  <Badge tone="danger">Crítica</Badge>
                ) : (
                  "—"
                )}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <Toggle
                  on={template.active}
                  onChange={() => toggleActive(template)}
                  disabled={!canToggle}
                  ariaLabel="Activa"
                />
                <span
                  style={{
                    font: `500 11px ${tokens.font.ui}`,
                    color: template.active
                      ? tokens.text.secondary
                      : tokens.text.disabled,
                  }}
                >
                  {template.active ? "Activa" : "Inactiva"}
                </span>
              </div>
              <span
                style={{
                  font: `400 11.5px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                {formatUpdated(template)}
              </span>
              <RowActionsCell>
                <RowEditButton
                  visible={canEdit}
                  onClick={() => {
                    setSaveError(undefined);
                    setSubmitError(undefined);
                    setEditing(template);
                  }}
                />
              </RowActionsCell>
            </div>
          );
        }}
      />

      {editing ? (
        <TemplateEditor
          key={editing.code}
          template={
            // Refleja el registro más reciente de la lista (tras guardar
            // sin cerrar, p. ej. al desactivar desde el editor) sin perder
            // la instancia que el usuario está editando si todavía no
            // llegó la respuesta.
            rows.find((row) => row.code === editing.code) ?? editing
          }
          canEdit={canEdit}
          submitting={mutations.edit.isPending}
          saveError={saveError}
          submitError={submitError}
          onUnhandledSubmitError={handleUnhandledSubmitError}
          onSave={(input) => attemptSave(editing, input, true)}
          onDeactivate={() => deactivateFromEditor(editing)}
          onCancel={() => {
            setSaveError(undefined);
            setSubmitError(undefined);
            setEditing(null);
          }}
          modal={{
            label: "Editar plantilla",
            width: 720,
            onClose: () => setEditing(null),
          }}
        />
      ) : null}

      <TemplateConfirmDialog
        open={pendingConfirm?.kind === "CONFIRM_CRITICAL"}
        title={confirmCriticalTitle(pendingConfirm?.name ?? "")}
        message={CONFIRM_CRITICAL_MESSAGE}
        confirmLabel="Sí, guardar"
        onCancel={() => setPendingConfirm(null)}
        onConfirm={confirmPending}
        busy={mutations.edit.isPending}
      />
      <TemplateConfirmDialog
        open={pendingConfirm?.kind === "CONFIRM_DEACTIVATE"}
        title={confirmDeactivateTitle(pendingConfirm?.name ?? "")}
        message={CONFIRM_DEACTIVATE_MESSAGE}
        confirmLabel="Sí, desactivar"
        onCancel={() => setPendingConfirm(null)}
        onConfirm={confirmPending}
        busy={mutations.edit.isPending}
      />
    </div>
  );
}
